import {
  DetailsHeader,
  DetailsList,
  DetailsRow,
  SelectionMode,
  Selection,
  Spinner,
  SpinnerSize,
} from 'office-ui-fabric-react';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { noSelectionKey } from '../../../../utilities/filterAndSortingsUtil';

export const KleMappingList = ({
  items,
  selectedHearingTypeKey,
  showSpinner,
  onChange,
  selectedItems,
}) => {
  const { t: translator } = useTranslation();

  const internalSelectedItems = useRef([]);
  const [selection] = useState(
    new Selection({
      selectionMode: SelectionMode.multiple,
      onSelectionChanged: () => {
        onSelectionChanged();
      },
    }),
  );

  const isItemSelected = useCallback(
    (key) => selectedItems.some((selectedItemKey) => selectedItemKey === key),
    [selectedItems],
  );

  const onSelectionChanged = useCallback(() => {
    const allCurrentKeys = selection.getItems().map(({ key }) => key);

    const selectedCurrentKeys = selection.getSelection().map(({ key }) => key);

    const newListSelected = [
      ...selectedCurrentKeys,
      ...internalSelectedItems.current.filter(
        (key) => !allCurrentKeys.includes(key),
      ),
    ];
    const newUniqueListSelected = [...new Set(newListSelected)];

    onChange(newUniqueListSelected);
  }, [selection, onChange]);

  // because the selection object in UI fabric is kind of crap and we use a function to mark which records are
  // selected, we need all this stuff at exactly this hook.
  if (items) {
    let didChange = false;
    if (items !== selection.getItems()) {
      selection.setChangeEvents(false);
      selection.setItems(items, false);
      selection.setChangeEvents(true, true);
      didChange = true;
    }

    if (didChange || items.length > 0) {
      let newSelectedItemKeys = items
        .map((item) => item.key)
        .filter(isItemSelected);

      // if the UI fabric list's current state for selection doesn't match (i.g. selection wasn't changed
      // through a click), change it manually
      if (
        !newSelectedItemKeys.every(selection.isKeySelected, selection) ||
        selection.getSelectedCount() !== newSelectedItemKeys.length
      ) {
        selection.setChangeEvents(false);
        selection.setAllSelected(false);
        newSelectedItemKeys.forEach((key) =>
          selection.setKeySelected(key, true),
        );
        selection.setChangeEvents(true, true);
      }
    }
  }

  internalSelectedItems.current = selectedItems;

  return (
    <div>
      {showSpinner ? (
        <Spinner size={SpinnerSize.large} />
      ) : selectedHearingTypeKey === noSelectionKey ? null : (
        <div className="kle-hierarchy-list" data-is-scrollable={true}>
          <DetailsList
            getKey={(item) => item.key}
            items={items}
            selection={selection}
            columns={columns(translator)}
            setKey={'kleHierarchies'}
            ariaLabel={translator('ariaLabel.kleMappingListLabel')}
            ariaLabelForSelectAllCheckbox={translator(
              'ariaLabel.listSelectAll',
            )}
            ariaLabelForSelectionColumn={translator(
              'ariaLabel.listSelectionColumn',
            )}
            onRenderDetailsHeader={(props) => {
              return (
                <DetailsHeader
                  {...props}
                  styles={listStyle}
                  selectAllVisibility={0}
                  ariaLabel={translator('ariaLabel.listHeader')}
                />
              );
            }}
            onRenderRow={(props) => {
              return (
                <div data-selection-toggle="true">
                  <DetailsRow tabIndex="0" {...props} styles={listStyle} />
                </div>
              );
            }}
            compact={true}
            selectionPreservedOnEmptyClick={true}
            width={'100%'}
            checkboxVisibility={1}
          />
        </div>
      )}
    </div>
  );
};

const columns = (translator) => [
  {
    key: 'name',
    name: translator('pages.adminKleMappings.kleNumber'),
    ariaLabel: translator('pages.adminKleMappings.kleNumber'),
    fieldName: 'name',
    data: 'string',
    isPadded: true,
    isResizable: false,
    minWidth: 650,
  },
];

const listStyle = {
  root: {
    backgroundColor: '#FAFAFA',
    width: '100%',
  },
};
